
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Plus } from "lucide-react";

interface Achievement {
  id: string;
  title: string;
  description?: string;
}

interface AchievementsInputProps {
  achievements: Achievement[];
  onAchievementsChange: (achievements: Achievement[]) => void;
}

export const AchievementsInput = ({
  achievements,
  onAchievementsChange,
}: AchievementsInputProps) => {
  const [newAchievementTitle, setNewAchievementTitle] = useState("");

  const addAchievement = () => {
    if (newAchievementTitle.trim() === "") return;

    const newAchievement = {
      id: Date.now().toString(),
      title: newAchievementTitle.trim(),
    };

    onAchievementsChange([...achievements, newAchievement]);
    setNewAchievementTitle("");
  };

  const removeAchievement = (id: string) => {
    onAchievementsChange(achievements.filter(achievement => achievement.id !== id));
  };

  const updateAchievementDescription = (id: string, description: string) => {
    onAchievementsChange(
      achievements.map(achievement => 
        achievement.id === id 
          ? { ...achievement, description } 
          : achievement
      )
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">Conquistas</h3>
      
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Input
            value={newAchievementTitle}
            onChange={(e) => setNewAchievementTitle(e.target.value)}
            placeholder="Adicionar nova conquista"
            className="flex-grow bg-gray-800 border-gray-700"
          />
          <Button 
            onClick={addAchievement}
            size="icon"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {achievements.length === 0 ? (
            <p className="text-gray-400 text-sm">Nenhuma conquista adicionada.</p>
          ) : (
            achievements.map((achievement) => (
              <Card key={achievement.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span>{achievement.title}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={() => removeAchievement(achievement.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2 px-4">
                  <Input
                    value={achievement.description || ""}
                    onChange={(e) => updateAchievementDescription(achievement.id, e.target.value)}
                    placeholder="Descrição da conquista (opcional)"
                    className="text-xs bg-gray-700 border-gray-600"
                  />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
